import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import PromptCard from '@/components/PromptCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Sparkles, TrendingUp, Clock, Layers, ChevronLeft, ChevronRight, Zap, PenLine, Flame, Star } from 'lucide-react';
import { toast } from 'sonner';
import TronGrid from '@/components/TronGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedPlatform, setSelectedPlatform] = useState(searchParams.get('platform') || '');
  const [selectedUser, setSelectedUser] = useState(searchParams.get('user') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [filterableUsers, setFilterableUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Sync URL params when filters change
  useEffect(() => {
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (selectedPlatform) params.platform = selectedPlatform;
    if (selectedUser) params.user = selectedUser;
    if (sort && sort !== 'newest') params.sort = sort;
    if (search) params.search = search;
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedPlatform, selectedUser, sort, search, setSearchParams]);

  // Read URL params on navigation (e.g. back from category page link)
  useEffect(() => {
    const cat = searchParams.get('category') || '';
    const plat = searchParams.get('platform') || '';
    const usr = searchParams.get('user') || '';
    setSelectedCategory(cat);
    setSelectedPlatform(plat);
    setSelectedUser(usr);
  }, [searchParams]);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 18, sort };
      if (selectedCategory) params.category = selectedCategory;
      if (selectedPlatform) params.platform = selectedPlatform;
      if (selectedUser) params.user_id = selectedUser;
      if (search.trim()) params.search = search.trim();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/prompts`, { params, headers });
      setPrompts(res.data.prompts);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch {
      setPrompts([]);
    }
    setLoading(false);
  }, [page, sort, selectedCategory, selectedPlatform, selectedUser, search, token]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [catRes, platRes, usersRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/filterable-users`),
        ]);
        setCategories(catRes.data);
        setPlatforms(platRes.data);
        setFilterableUsers(usersRes.data.users || []);
      } catch {}
    };
    loadMeta();
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPrompts();
  };

  const handleVoteChange = (promptId, data) => {
    setPrompts(prev => prev.map(p =>
      p.id === promptId ? { ...p, upvotes: data.score, user_vote: data.vote } : p
    ));
  };

  const handleBookmarkChange = (promptId, data) => {
    setPrompts(prev => prev.map(p =>
      p.id === promptId ? { ...p, has_bookmarked: data.bookmarked } : p
    ));
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
        <TronGrid />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 rounded-full px-3 py-1">
                <Zap className="w-3 h-3 mr-1.5" /> Community Powered
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="hero-title">
              Prompt<span className="text-primary">Vault</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl leading-relaxed">
              Discover, share, and upvote the best AI prompts. Built by the community, for the community.
            </p>

            {/* Search + Contribute */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <form onSubmit={handleSearch} className="flex gap-2 flex-1" data-testid="search-form">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search prompts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-11 rounded-full bg-card border-border/80"
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" className="rounded-full h-11 px-6 shadow-lg shadow-primary/20" data-testid="search-btn">
                  Search
                </Button>
              </form>
              <Link to="/submit">
                <Button
                  variant="outline"
                  className="h-11 rounded-full gap-2 px-5 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all font-medium shrink-0"
                  data-testid="hero-contribute-btn"
                >
                  <PenLine className="w-4 h-4" /> Contribute Your Prompt
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{total}</div>
                <div className="text-xs text-muted-foreground">Prompts</div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{platforms.length}</div>
                <div className="text-xs text-muted-foreground">Platforms</div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{categories.length}</div>
                <div className="text-xs text-muted-foreground">Categories</div>
              </div>
            </div>

            {/* Tagline under stats */}
            <p className="mt-4 text-sm text-muted-foreground">
              Share your prompts. Grow your platforms. Build your Karma.
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Feed */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-8 p-4 rounded-xl bg-card border border-border/50" data-testid="filter-bar">
          <Select value={selectedPlatform || undefined} onValueChange={(v) => { setSelectedPlatform(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px] rounded-full h-9 text-sm" data-testid="filter-platform">
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={selectedCategory || undefined} onValueChange={(v) => { setSelectedCategory(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px] rounded-full h-9 text-sm" data-testid="filter-category">
              <Layers className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {filterableUsers.length > 0 && (
            <Select value={selectedUser || undefined} onValueChange={(v) => { setSelectedUser(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[175px] rounded-full h-9 text-sm" data-testid="filter-user">
                <Star className="w-3.5 h-3.5 mr-1.5 fill-amber-500 text-amber-500" />
                <SelectValue placeholder="Karmic Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {filterableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.username}
                    <span className="ml-2 text-xs text-muted-foreground">⚡{u.karma}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-1 ml-auto">
            {[
              { value: 'newest', label: 'Newest', icon: Clock },
              { value: 'popular', label: 'Popular', icon: TrendingUp },
              { value: 'trending', label: 'Trending', icon: Flame },
            ].map(s => (
              <Button
                key={s.value}
                variant={sort === s.value ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-full h-9 gap-1.5 text-xs ${sort === s.value ? 'shadow-lg shadow-primary/20' : ''}`}
                onClick={() => { setSort(s.value); setPage(1); }}
                data-testid={`sort-${s.value}`}
              >
                <s.icon className="w-3.5 h-3.5" /> {s.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Active filters */}
        {(selectedCategory || selectedPlatform || search) && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {search && <Badge variant="outline" className="rounded-full text-xs cursor-pointer" onClick={() => { setSearch(''); setPage(1); }}>{search} &times;</Badge>}
            {selectedCategory && <Badge variant="outline" className="rounded-full text-xs cursor-pointer" onClick={() => { setSelectedCategory(''); setPage(1); }}>{selectedCategory} &times;</Badge>}
            {selectedPlatform && <Badge variant="outline" className="rounded-full text-xs cursor-pointer" onClick={() => { setSelectedPlatform(''); setPage(1); }}>
              {platforms.find(p => p.id === selectedPlatform)?.name} &times;
            </Badge>}
            <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => { setSearch(''); setSelectedCategory(''); setSelectedPlatform(''); setPage(1); }}>
              Clear all
            </Button>
          </div>
        )}

        {/* Prompt Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5 animate-pulse">
                <div className="flex gap-2 mb-3"><div className="h-5 w-16 bg-muted rounded-full" /><div className="h-5 w-20 bg-muted rounded-full" /></div>
                <div className="h-5 w-3/4 bg-muted rounded mb-2" />
                <div className="h-4 w-full bg-muted rounded mb-1" />
                <div className="h-4 w-2/3 bg-muted rounded mb-4" />
                <div className="h-8 w-full bg-muted rounded" />
              </div>
            ))}
          </div>
        ) : prompts.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Sparkles className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No prompts yet</h3>
            <p className="text-sm text-muted-foreground mb-6">Be the first to share a prompt with the community!</p>
            <Link to="/submit">
              <Button className="rounded-full shadow-lg shadow-primary/20" data-testid="empty-submit-btn">Share a Prompt</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prompt-grid">
            {prompts.map(prompt => (
              <PromptCard key={prompt.id} prompt={prompt} onVoteChange={handleVoteChange} onBookmarkChange={handleBookmarkChange} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-full" data-testid="prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground px-3">
              Page {page} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-full" data-testid="next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
